import type { ActionDefinitionT } from "@/types/database/actions";
import type { StateKeyT, StatefulDiaryT } from "@/types/database/diaries";
import type { ProfileT } from "@/types/database/profiles";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { useAuth } from "../../../../../utils/auth";
import { useSelectActionsWordsQuery } from "../api/selectActionWords";
import { useSelectEmailSignaturesQuery } from "../api/selectEmailSignature";
import { useSelectStateWordsQuery } from "../api/selectStateWords";
import useTakeAction from "../api/useTakeAction";
import { buildEmail } from "../services/useEmailBuilder";

const ActionDetailSchemaZ = z.object({
	diary_status: z.string(),
	diary_id: z.string().uuid(),
	action_id: z.string().uuid(),
	from_email: z.string().email(),
	to_email: z.string(),
	cc_email: z.string(),
	action_key: z.string(),
	body: z.string(),
	subject: z
		.string()
		.min(1, "Subject is required")
		.max(100, "Subject is too long"),
});

export type ActionDetailSchemaT = z.infer<typeof ActionDetailSchemaZ>;
interface Props {
	actionId: string;
	action: ActionDefinitionT;
	diary: StatefulDiaryT;
	profile: ProfileT;
}

const useActionEmailHook = (props: Props) => {
	const auth = useAuth();
	const takeAction = useTakeAction();
	const { data: actionWords } = useSelectActionsWordsQuery(
		props.action.action_key,
		auth.id,
	);
	const { data: stateWords } = useSelectStateWordsQuery(
		props.diary.diary_status,
		auth.id,
	);
	const { data: emailSignature } = useSelectEmailSignaturesQuery(auth.id);

	const { data: body } = useQuery({
		queryKey: [
			"email-builder",
			props.diary.diary_status,
			props.action.action_key,
			auth.id,
		],
		queryFn: () => {
			return buildEmail({
				toName: props.diary.patient_info?.patient_name ?? "Patient",
				start: stateWords ?? props.diary.state.email_words,
				end: actionWords ?? props.action?.email_words ?? "",
				from:
					emailSignature?.email_signature ??
					`Kind regards,
${props.profile.name}`,
				salutation: emailSignature?.email_salutation ?? "Dear",
				diaryId:
					props.action?.action_key === "DISCHARGE"
						? props.diary.diary_id
						: undefined,
				patientId:
					props.action?.action_key === "DISCHARGE"
						? props.diary.patient_id
						: undefined,
			});
		},
		enabled: !!stateWords && !!actionWords && !!emailSignature,
	});

	const form = useForm<ActionDetailSchemaT>({
		resolver: zodResolver(ActionDetailSchemaZ),
		defaultValues: {
			diary_status: props.diary.diary_status,
			action_id: props.actionId,
			from_email: "clinician@patient-watch.com",
			action_key: props.action?.action_key,
			diary_id: props.diary.diary_id,
			subject: `Patient Watch ${props.action?.action_name}`,
			body: body ?? "",
			cc_email: props.diary?.contact_email ?? "",
			to_email: props.diary.patient_info?.patient_email ?? "",
		},
	});

	// Update form when body changes
	React.useEffect(() => {
		if (body) {
			form.setValue("body", body);
		}
	}, [body, form]);

	const onSubmit: SubmitHandler<ActionDetailSchemaT> = async (formData) => {
		takeAction.mutate({
			...props,
			...formData,
			action_taker_id: auth.id,
		});
	};

	return { onSubmit, form };
};

export default useActionEmailHook;
